import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { DeploymentClass } from 'src/app/models/instance-class';
import { Instance } from 'src/app/models/instance/instance';
import { Constants } from 'src/app/models/constants';
import { BlueprintCard } from './service-definition-cards/service-definition-cards.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false
})
export class HomeComponent implements OnInit {

  activeServicesCount = 0;
  environmentsCount = 0;
  blueprintCards: BlueprintCard[] = [];
  isLoading = true;

  private readonly authService = inject(AuthService);
  private readonly blueprintsService = inject(BlueprintsService);
  private readonly instancesService = inject(InstancesService);
  private readonly solutionsService = inject(SolutionsService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const tenantId = this.authService.principal.provider;
    forkJoin({
      blueprints: this.blueprintsService.getBlueprints(tenantId).pipe(
        map(blueprints => blueprints.filter(blueprint => blueprint.tenantId === tenantId && !blueprint.demo)),
        tap(blueprints => this.activeServicesCount = blueprints.length)
      ),
      solutionNames: this.getSolutionNames()
    }).pipe(
      // Cards render first with the instance count left null, so the card shows a placeholder until instances are counted
      tap(({ blueprints, solutionNames }) => {
        this.blueprintCards = blueprints.map(blueprint => ({
          id: blueprint.id,
          name: blueprint.name,
          instancesCount: null,
          solutionId: blueprint.solutionId,
          solutionName: solutionNames.get(blueprint.solutionId)
        }));
        this.isLoading = false;
      }),
      // Instances are requested only after the blueprints response is available
      switchMap(() => this.instancesService.getInstances(tenantId).pipe(
        // On failure, counts fall back to zero instead of showing the placeholder indefinitely
        catchError(() => of([] as Instance[]))
      )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(instances => {
      const relevantInstances = instances.filter(instance =>
        instance.tenantId === tenantId
        && !Constants.EXCLUDED_INSTANCE_TYPES.includes(instance.type ?? '')
        && instance.status !== DeployStatus.TERMINATED);

      const instancesByBlueprint = new Map<string, Instance[]>();
      relevantInstances.forEach(instance => {
        const blueprintId = instance.blueprintId ?? '';
        instancesByBlueprint.set(blueprintId, [...(instancesByBlueprint.get(blueprintId) ?? []), instance]);
      });

      // A blueprint's instances are counted only when at least one of them is deploymentClass D; otherwise its count stays at zero
      const countByBlueprint = new Map<string, number>();
      instancesByBlueprint.forEach((blueprintInstances, blueprintId) => {
        const hasClassD = blueprintInstances.some(instance => instance.deploymentClass === DeploymentClass.D);
        countByBlueprint.set(blueprintId, hasClassD ? blueprintInstances.length : 0);
      });

      this.blueprintCards = this.blueprintCards.map(card => ({
        ...card,
        instancesCount: countByBlueprint.get(card.id) ?? 0
      }));
      this.environmentsCount = this.blueprintCards.reduce((total, card) => total + (card.instancesCount ?? 0), 0);
    }, () => this.isLoading = false);
  }

  /**
   * Loads all solutions in a single call and maps solution id to name.
   */
  private getSolutionNames(): Observable<Map<string, string>> {
    return this.solutionsService.getSolutions().pipe(
      map(solutions => new Map(solutions.map(solution => [solution.id, solution.name] as [string, string])))
    );
  }
}
